import { X, CheckCircle, XCircle, Lightbulb, BookOpen } from 'lucide-react';
import { FeatureGuide } from '../data/guides';

interface HelpPanelProps {
  guide: FeatureGuide;
  onClose: () => void;
}

export default function HelpPanel({ guide, onClose }: HelpPanelProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
      <div
        className="h-full w-full max-w-lg bg-white shadow-xl flex flex-col animate-slide-in-right safe-area-inset-top safe-area-inset-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-primary-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{guide.title} Guide</h2>
              <p className="text-sm text-slate-500">{guide.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-primary-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* What it can do */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-success-600" />
              <h3 className="font-semibold text-slate-900">{guide.canDo.title}</h3>
            </div>
            <ul className="space-y-2">
              {guide.canDo.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-success-500 mt-1">+</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* What it cannot do */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="w-5 h-5 text-slate-400" />
              <h3 className="font-semibold text-slate-900">{guide.cannotDo.title}</h3>
            </div>
            <ul className="space-y-2">
              {guide.cannotDo.items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-500">
                  <span className="text-slate-400 mt-1">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* How to use */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-primary-600" />
              <h3 className="font-semibold text-slate-900">How to use</h3>
            </div>
            <div className="space-y-4">
              {guide.howTo.map((howTo, idx) => (
                <div key={idx} className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-800 mb-2">{howTo.title}</h4>
                  <ol className="space-y-1.5">
                    {howTo.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium flex items-center justify-center">
                          {stepIdx + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </section>

          {/* Pro tips */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-slate-900">Pro tips</h3>
            </div>
            <ul className="space-y-2">
              {guide.tips.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600 bg-amber-50 p-3 rounded-lg">
                  <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={onClose}
            className="btn btn-secondary w-full"
          >
            Close Guide
          </button>
        </div>
      </div>
    </div>
  );
}
